import React, { useState } from 'react';

const SocialMediaTab = ({ person, updateField }) => {
  const [newAccount, setNewAccount] = useState({ platform: 'instagram', username: '', link: '', tag: '' });

  const socialMedia = person.socialMedia || {};
  const platforms = ['instagram', 'tiktok', 'twitter', 'facebook', 'youtube', 'snapchat', 'other'];

  const handleAddAccount = () => {
    if (!newAccount.username || !newAccount.link) return;

    const platform = newAccount.platform;
    const account = {
      id: Date.now().toString(),
      platform: platform,
      username: newAccount.username,
      link: newAccount.link,
      tag: newAccount.tag,
    };

    const updatedSocialMedia = {
      ...socialMedia,
      [platform]: [...(socialMedia[platform] || []), account],
    };

    updateField('socialMedia', updatedSocialMedia);
    setNewAccount({ platform: 'instagram', username: '', link: '', tag: '' });
  };

  const handleRemoveAccount = (platform, accountId) => {
    const updatedSocialMedia = {
      ...socialMedia,
      [platform]: socialMedia[platform].filter(acc => acc.id !== accountId),
    };
    updateField('socialMedia', updatedSocialMedia);
  };

  const getAllAccounts = () => {
    const accounts = [];
    Object.keys(socialMedia).forEach(platform => {
      socialMedia[platform]?.forEach(account => {
        accounts.push({ ...account, platformKey: platform });
      });
    });
    return accounts;
  };

  const allAccounts = getAllAccounts();

  return (
    <div className="max-w-4xl mx-auto space-y-4 p-4">
      {/* Add New Account Card */}
      <div className="tech-card p-4">
        <h3 className="tech-section-title mb-4 flex items-center gap-2">
          <svg className="w-4 h-4 text-tech-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          ADD ACCOUNT
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-mono text-tech-textMuted mb-2">PLATFORM</label>
            <select
              value={newAccount.platform}
              onChange={(e) => setNewAccount({ ...newAccount, platform: e.target.value })}
              className="tech-input"
            >
              {platforms.map(platform => (
                <option key={platform} value={platform}>
                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-mono text-tech-textMuted mb-2">TAG / LABEL</label>
            <input
              type="text"
              value={newAccount.tag}
              onChange={(e) => setNewAccount({ ...newAccount, tag: e.target.value })}
              placeholder="e.g., Main, Private"
              className="tech-input"
            />
          </div>
          <div>
            <label className="block text-xs font-mono text-tech-textMuted mb-2">USERNAME</label>
            <input
              type="text"
              value={newAccount.username}
              onChange={(e) => setNewAccount({ ...newAccount, username: e.target.value })}
              placeholder="@username"
              className="tech-input"
            />
          </div>
          <div>
            <label className="block text-xs font-mono text-tech-textMuted mb-2">PROFILE LINK</label>
            <input
              type="url"
              value={newAccount.link}
              onChange={(e) => setNewAccount({ ...newAccount, link: e.target.value })}
              placeholder="https://..."
              className="tech-input"
            />
          </div>
          <div className="md:col-span-2">
            <button
              onClick={handleAddAccount}
              className="w-full tech-btn-primary py-2"
            >
              ADD ACCOUNT
            </button>
          </div>
        </div>
      </div>

      {/* Accounts List */}
      <div className="tech-card p-4">
        <h3 className="tech-section-title mb-4">ACCOUNTS ({allAccounts.length})</h3>
        {allAccounts.length === 0 ? (
          <div className="text-center py-8">
            <svg className="w-12 h-12 text-tech-textDim mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
            </svg>
            <p className="text-tech-textDim text-sm">NO ACCOUNTS ADDED</p>
          </div>
        ) : (
          <div className="space-y-2">
            {allAccounts.map(account => (
              <div
                key={account.id}
                className="bg-tech-surface rounded-lg p-3 flex items-center justify-between hover:bg-tech-elevated transition"
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 rounded bg-tech-accent/10 border border-tech-accent/30 flex items-center justify-center">
                    <span className="text-tech-accent font-bold text-sm">
                      {account.platform.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-tech-text font-medium">{account.username}</p>
                      {account.tag && (
                        <span className="px-2 py-0.5 bg-tech-accent/10 text-tech-accent text-xs rounded font-mono">
                          {account.tag}
                        </span>
                      )}
                    </div>
                    <p className="text-tech-textDim text-xs capitalize">{account.platform}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={account.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-tech-textMuted hover:text-tech-accent hover:bg-tech-surface rounded transition"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <button
                    onClick={() => handleRemoveAccount(account.platformKey, account.id)}
                    className="p-2 text-tech-textMuted hover:text-tech-danger hover:bg-tech-danger/10 rounded transition"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialMediaTab;
